import React, { useState } from "react";
import InputBox from "../Components/InputBox";
import SelectInput from "../Components/SelectInput";
import { addNewDoc } from "../../../utils/firebase/Firebase.utils";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";

export default function IncidentForm({ setAddMode }) {
  const currentUser = useSelector((state) => state.userObject);
  const [activeLink, setActiveLink] = React.useState("No");
  const [formData, setFormData] = React.useState({});
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [selectedIncident, setSelectedIncident] = React.useState("");
  const [editing, setEditing] = useState(false);

  const links = ["Yes", "No"];

  const incidentTypes = [
    "Theft",
    "Robbery",
    "Traffic Accident",
    "Vandalism",
    "Assault",
    "Suspicious Activity",
    "Homicide/Murder",
    "Fire",
    "Alarm & Scandal",
    "Others",
  ];
  const incidentOptions = incidentTypes.map((incident) => {
    return {
      label: incident,
      value: incident,
    };
  });

  const handleClose = () => {
    if (editing) {
      if (
        window.confirm(
          "Your changes will not be saved? Do you wish to continue?"
        )
      ) {
        setAddMode(false);
      } else {
        return;
      }
    } else {
      setAddMode(false);
    }
  };

  const handleChange = (e) => {
    setEditing(true);
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    const docObject = {
      ...formData,
      policeReported: activeLink,
      incidentType: selectedIncident,
      createdBy: currentUser.uid,
      createdName: currentUser.displayName,
      createdEmail: currentUser.email,
      updateType: "Incident Report Created",
      createdAt: new Date().toLocaleString(),
      status: "pending",
      history: [
        {
          action: "Created incident report",
          at: new Date().toLocaleString(),
          by: currentUser.displayName,
        },
      ],
    };

    await addNewDoc("incidentReports", docObject);
    toast.success("Incident Report Submitted Successfully");
    setIsProcessing(false);
    setAddMode(false);
  };

  const handleSelectIncident = (selected) => {
    setEditing(true);
    setSelectedIncident(selected.value);
  };

  return (
    <div className="min-h-screen p-6 relative">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="title-header">New Incident Report</div>
            <div className="flex items-center gap-4">
              <button
                className="p-3 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={handleClose}
              >
                <svg
                  className="w-6 h-6 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Incident Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Incident Information Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="border-b border-gray-100 p-4">
                  <h3 className="font-semibold text-lg text-[#363062]">
                    Incident Information
                  </h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <div className="text-sm text-gray-500 mb-1">
                        Type of Incident
                      </div>
                      <SelectInput
                        options={incidentOptions}
                        name="type"
                        placeholder="Select incident type"
                        onChange={(selected) => handleSelectIncident(selected)}
                        editable={!isProcessing}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Location</div>
                      <input
                        type="text"
                        name="location"
                        className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#363062]"
                        placeholder="Enter location of incident"
                        onChange={handleChange}
                        disabled={isProcessing}
                      />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">
                        Police Reported?
                      </div>
                      <div className="flex items-center gap-2">
                        {links.map((link, index) => (
                          <button
                            type="button"
                            className={`px-4 py-2 rounded-lg font-medium transition-all ${
                              activeLink === link
                                ? "bg-[#363062] text-white"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                            key={index}
                            onClick={() => setActiveLink(link)}
                          >
                            {link}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">
                        Date of Incident
                      </div>
                      <input
                        type="date"
                        name="date"
                        className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#363062]"
                        onChange={handleChange}
                        disabled={isProcessing}
                      />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">
                        Time of Incident
                      </div>
                      <input
                        type="time"
                        name="time"
                        className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#363062]"
                        onChange={handleChange}
                        disabled={isProcessing}
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-[#363062]">
                      Incident Description
                    </h4>
                    <textarea
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#363062] resize-none"
                      rows={15}
                      name="description"
                      placeholder="Provide a detailed description of the incident..."
                      onChange={handleChange}
                      disabled={isProcessing}
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Reporter Info */}
            <div className="space-y-6">
              {/* Reporter Info Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="border-b border-gray-100 p-4">
                  <h3 className="font-semibold text-lg text-[#363062]">
                    Reporter Information
                  </h3>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-gray-500 mb-1">
                        Reported By
                      </div>
                      <div className="font-medium">
                        {currentUser.displayName || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Email</div>
                      <div className="font-medium">
                        {currentUser.email || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Date</div>
                      <div className="font-medium">
                        {new Date().toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="border-b border-gray-100 p-4">
                  <h3 className="font-semibold text-lg text-[#363062]">
                    Submit Report
                  </h3>
                </div>
                <div className="p-6">
                  <p className="text-gray-500 mb-4">
                    Please review all information before submission. Once
                    submitted, the incident will be processed for investigation.
                  </p>
                  <button
                    className="w-full px-6 py-3 bg-[#8FCD00] text-white rounded-lg hover:bg-opacity-90 font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleSubmit}
                    disabled={isProcessing}
                  >
                    {isProcessing ? "Submitting..." : "Submit Incident Report"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
